import {
  DEFAULT_SBEF_CUSTOM_GROUPS,
  SBEF_CUSTOM_GROUP_IDS,
  SbefScopeGroupKey,
  sbefScopeGroupKeys,
} from '@shared/construction/sbef-custom-groups.data';
import { SbefCode, SbefGroupCode } from '@shared/construction/sbef.model';
import {
  getSbefGroupByCode,
  getSbefScopeGroupKeyForCode,
  NO_SBEF_GROUP_KEY,
  normalizeSbefScopeGroupKey,
  SbefResolvedGroupKind,
  SITE_OPERATIONS_SBEF_GROUP_KEY,
  SITE_OPERATIONS_SBEF_GROUP_LABEL,
} from '@shared/helpers/sbef-grouping.helpers';
import { SBEF_SELECTABLE_SCOPE_GROUP_KEYS } from '@shared/helpers/sbef-scope.helpers';
import {
  getSbefCodeFromElementOrRecipe,
  isSbefCode,
} from '@shared/helpers/sbef.helpers';
import {
  Results,
  ResultsRecord,
  SbefGroupResults,
  SbefGroupResultsRecord,
} from '@shared/models/unit.model';
import {
  compressConversionFactors,
  sumConversionFactors,
} from '@shared/helpers/conversion-factors.helpers';
import { projectGuards } from '@shared/helpers/guards/project-guards.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { OneOfElements } from '@shared/models/project.model';
import { Recipe } from '@shared/models/recipe.model';
import { filterObject } from './object.helpers';
import { isEqual } from 'lodash';

export { getSbefScopeGroupKeyForCode };

/** Deprecated SBEF bucket keys folded into group 9 when persisting breakdowns. */
export const LEGACY_SBEF_GROUP_RESULT_KEYS = [
  NO_SBEF_GROUP_KEY,
  ...SBEF_CUSTOM_GROUP_IDS,
] as const;

/** Keys that hold nested per-group {@link Results} on a {@link SbefGroupResults} row. */
export const SBEF_GROUP_BREAKDOWN_KEYS = new Set<string>([
  ...sbefScopeGroupKeys,
  ...LEGACY_SBEF_GROUP_RESULT_KEYS,
]);

const stripSbefGroupBreakdown = (results: Results): Results => {
  const hasBreakdown = [...SBEF_GROUP_BREAKDOWN_KEYS].some(
    (key) => key in results,
  );
  if (!hasBreakdown) return results;
  return filterObject(
    results,
    (_, key) => !SBEF_GROUP_BREAKDOWN_KEYS.has(key),
  ) as Results;
};

/**
 * Type guard for the canonical SBEF scope group keys (standard `'0'`–`'9'` plus the custom
 * A5.1 / A5.2-5 groups). Used to narrow resolved group keys before they enter the breakdown.
 */
export const isSbefScopeGroupKey = (
  key: string | null | undefined,
): key is SbefScopeGroupKey =>
  key != null && sbefScopeGroupKeys.includes(key as SbefScopeGroupKey);

/**
 * Merge legacy General / 100-series buckets into group 9 when reading stored breakdowns.
 */
export const normalizeSbefGroupResultsRecord = (
  groups: SbefGroupResultsRecord,
): SbefGroupResultsRecord => {
  const out: SbefGroupResultsRecord = {};
  for (const [key, value] of Object.entries(groups)) {
    if (!value) continue;
    const normalized = normalizeSbefScopeGroupKey(key) as SbefScopeGroupKey;
    out[normalized] = sumConversionFactors(out[normalized], value);
  }
  return out;
};

/**
 * Resolve the SBEF scope group key for an element, matching the element/product list "group by
 * code" grouping: standard groups (`'0'`–`'8'`) or group 9 (Site operations) for general
 * content, A5 custom codes, and other 100-series codes.
 */
export const getElementSbefGroupKey = (
  element: OneOfElements | Recipe,
): SbefScopeGroupKey => {
  const code = getSbefCodeFromElementOrRecipe(element);
  const key = getSbefScopeGroupKeyForCode(code, DEFAULT_SBEF_CUSTOM_GROUPS);
  return isSbefScopeGroupKey(key) ? key : SITE_OPERATIONS_SBEF_GROUP_KEY;
};

/**
 * Map a scope group key to the SBEF code to persist on an element so it lands in that group.
 * Standard groups (`'0'`–`'9'`) are themselves valid codes; a custom group uses its first member
 * code. Ungrouped content has no stored code and still lands in Site operations (group 9).
 */
export const getSbefCodeForScopeGroupKey = (
  key: SbefScopeGroupKey,
): SbefCode | undefined => {
  const resolved = getSbefGroupByCode(key, DEFAULT_SBEF_CUSTOM_GROUPS);
  if (resolved.kind === SbefResolvedGroupKind.Custom)
    return resolved.group.sbefCodes[0];
  return isSbefCode(key) ? key : undefined;
};

type NewElementSbefContext = {
  /** SBEF group row currently selected in the element list, if any. */
  selectedGroupKey: string | null;
  /** SBEF code of the currently selected element, if any. */
  selectedElementSbefCode: SbefCode | undefined;
  /** Scope groups toggled visible via the scope selector. */
  selectedSbefGroups: readonly SbefScopeGroupKey[];
};

/**
 * Resolve which SBEF code a newly created element should get so it always lands in a group that is
 * currently visible under the scope selector (DEV-3507). Priority: the selected group → the
 * selected element's group → group 9 ({@link SbefGroupCode.CommonWorks}) → the first visible group.
 * Returns `undefined` (element stays ungrouped, which still folds into Site operations) when no
 * scope filter is active.
 */
export const resolveVisibleSbefCodeForNewElement = ({
  selectedGroupKey,
  selectedElementSbefCode,
  selectedSbefGroups,
}: NewElementSbefContext): SbefCode | undefined => {
  const filterActive = isSbefGroupFilterActive(selectedSbefGroups);
  const isVisible = (key: SbefScopeGroupKey) =>
    !filterActive || selectedSbefGroups.includes(key);

  if (isSbefScopeGroupKey(selectedGroupKey) && isVisible(selectedGroupKey))
    return getSbefCodeForScopeGroupKey(selectedGroupKey);

  if (selectedElementSbefCode) {
    const elementGroup = getSbefScopeGroupKeyForCode(
      selectedElementSbefCode,
      DEFAULT_SBEF_CUSTOM_GROUPS,
    );
    if (isSbefScopeGroupKey(elementGroup) && isVisible(elementGroup)) {
      return selectedElementSbefCode;
    }
  }

  if (!filterActive) return selectedElementSbefCode;

  if (isVisible(SbefGroupCode.CommonWorks)) return SbefGroupCode.CommonWorks;

  const firstVisibleGroup = sbefScopeGroupKeys.find(isVisible);
  return firstVisibleGroup
    ? getSbefCodeForScopeGroupKey(firstVisibleGroup)
    : undefined;
};

/**
 * Read the per-SBEF-group breakdown spread on a {@link Results} row (the {@link SbefScopeGroupKey}
 * keys), if any.
 */
export const getSbefGroupResults = (
  results?: Results,
): SbefGroupResultsRecord => {
  if (!results) return {};
  const row = results as SbefGroupResults;
  const out: SbefGroupResultsRecord = {};
  for (const key of sbefScopeGroupKeys) {
    const value = row[key];
    if (value) out[key] = value;
  }
  const legacyRow = row as SbefGroupResults &
    Record<string, Results | undefined>;
  for (const key of Object.keys(legacyRow)) {
    if (key === NO_SBEF_GROUP_KEY) {
      const value = legacyRow[key];
      if (value) {
        out[SITE_OPERATIONS_SBEF_GROUP_KEY] = sumConversionFactors(
          out[SITE_OPERATIONS_SBEF_GROUP_KEY],
          value,
        );
      }
    }
  }
  return normalizeSbefGroupResultsRecord(out);
};

/**
 * Rewrite a stored {@link Results} row so legacy General / A5 buckets are folded into group 9
 * and deprecated keys are dropped from the persisted document.
 */
export const rewritePersistedSbefGroupResultsRow = (
  results: Results,
): Results => {
  const groups = getSbefGroupResults(results);
  const flat = stripSbefGroupBreakdown(results);
  return setSbefGroupResults(flat, groups);
};

/**
 * Apply {@link rewritePersistedSbefGroupResultsRow} to every row in a {@link ResultsRecord}.
 */
export const rewritePersistedSbefGroupResultsRecord = (
  record: ResultsRecord,
): ResultsRecord | undefined => {
  let changed = false;
  const out: ResultsRecord = { ...record };
  for (const [id, row] of Object.entries(record)) {
    if (!row) continue;
    const next = rewritePersistedSbefGroupResultsRow(row);
    if (!isEqual(next, row)) {
      out[id] = next;
      changed = true;
    }
  }
  return changed ? out : undefined;
};

/**
 * Strip the spread per-SBEF-group buckets ({@link SbefScopeGroupKey} keys) so only the flat
 * numeric {@link Results} remain. Returns the same reference when there is nothing to strip.
 */
export const getFlatResults = (
  results: Results | SbefGroupResults,
): Results => {
  return stripSbefGroupBreakdown(results);
};

/**
 * Shape persisted on {@link Project.results}: the active-version rollup without per-SBEF-group
 * buckets. Version and proposal {@link ResultsRecord}s keep the breakdown so GET /projects can
 * select the column as-is.
 */
export const slimProjectResults = (results: Results | undefined): Results =>
  getFlatResults(results ?? {});

/**
 * Compress each group bucket and drop empty buckets.
 */
export const compressSbefGroupResultsRecord = (
  groups: SbefGroupResultsRecord,
): SbefGroupResultsRecord => {
  const out: SbefGroupResultsRecord = {};
  for (const key of sbefScopeGroupKeys) {
    const value = groups[key];
    if (!value) continue;
    const compressed = compressConversionFactors(value);
    if (Object.keys(compressed).length === 0) continue;
    out[key] = compressed;
  }
  return out;
};

/**
 * Spread a (compressed) per-SBEF-group breakdown onto a flat {@link Results}. When the
 * breakdown is empty the flat results are returned without any group keys.
 */
export const setSbefGroupResults = (
  results: Results,
  groups: SbefGroupResultsRecord,
): SbefGroupResults => {
  const flat = getFlatResults(results);
  const cleaned = compressSbefGroupResultsRecord(groups);
  if (Object.keys(cleaned).length === 0) return flat;
  return { ...flat, ...cleaned };
};

/**
 * Sum several per-SBEF-group breakdowns into one (summing {@link Results} per group code).
 */
export const sumSbefGroupResultsRecords = (
  ...records: (SbefGroupResultsRecord | undefined)[]
): SbefGroupResultsRecord => {
  const out: SbefGroupResultsRecord = {};
  for (const record of records) {
    if (!record) continue;
    for (const key of sbefScopeGroupKeys) {
      const value = record[key];
      if (!value) continue;
      out[key] = sumConversionFactors(out[key], value);
    }
  }
  return out;
};

/**
 * Build the per-SBEF-group breakdown for a node from its already-computed child rows.
 * Each child element is bucketed by its own standard SBEF group code; product-element
 * children (which carry no SBEF code) fold into the owning node's group via `ownGroupKey`.
 * Children that do not resolve to a standard group code are left out of the breakdown.
 * @param ownGroupKey - The owning node's group code, used for product-element children.
 * @param children - Visible children whose rows are present in `record`.
 * @param record - Flat results record to read child totals from.
 */
export const buildSbefGroupBuckets = (
  ownGroupKey: SbefScopeGroupKey | undefined,
  children: OneOfElements[],
  record: ResultsRecord,
): SbefGroupResultsRecord => {
  const buckets: SbefGroupResultsRecord = {};
  for (const child of children) {
    const childRow = record[child.id];
    if (!childRow) continue;
    const key = projectGuards.is.element(child)
      ? getElementSbefGroupKey(child)
      : ownGroupKey;
    if (!key) continue;
    buckets[key] = sumConversionFactors(buckets[key], getFlatResults(childRow));
  }
  return buckets;
};

/**
 * True when an SBEF group selection should actually filter (a strict subset of the selectable
 * scope groups). An empty selection and selecting all {@link SBEF_SELECTABLE_SCOPE_GROUP_KEYS}
 * both mean "no SBEF scope" (show flat totals / full list).
 */
export const isSbefGroupFilterActive = (
  selectedGroups: readonly SbefScopeGroupKey[],
): boolean =>
  selectedGroups.length > 0 &&
  selectedGroups.length < SBEF_SELECTABLE_SCOPE_GROUP_KEYS.length;

/**
 * Comma-separated SBEF scope group keys for the current selection, in canonical order.
 */
export const getSbefGroupSelectionLabel = (
  selectedGroups: readonly SbefScopeGroupKey[],
): string =>
  sbefScopeGroupKeys
    .filter((id) => selectedGroups.includes(id))
    .map((id) =>
      id === SITE_OPERATIONS_SBEF_GROUP_KEY
        ? SITE_OPERATIONS_SBEF_GROUP_LABEL
        : id,
    )
    .join(', ');

/**
 * Collapse a {@link Results} row to the selected SBEF groups. With no selection or all
 * groups selected, the flat results are returned unchanged. Otherwise the selected group buckets are summed
 * into a flat {@link Results}; rows without a stored breakdown contribute nothing, and
 * elements without a standard group code are not represented in any bucket.
 */
export const filterResultsBySbefGroups = (
  results: Results,
  selectedGroups: readonly SbefScopeGroupKey[],
): Results => {
  if (!isSbefGroupFilterActive(selectedGroups)) return getFlatResults(results);
  const groups = normalizeSbefGroupResultsRecord(getSbefGroupResults(results));
  if (Object.keys(groups).length === 0) return {};
  return sumConversionFactors(
    ...selectedGroups.map((key) => groups[key]).filter(guards.is.defined),
  );
};
